import { useEffect, useState } from "react";
import { getCard } from "../../content/lookups.js";
import { getStage, listStages } from "../../content/stages.js";
import { useGame } from "../../app/GameProvider.js";
import { CardView } from "../../components/CardView.js";
import {
  BRING_IN_CARD_IDS,
  bringInUnlockAt,
  countClearedStages,
  isBringInUnlocked,
} from "../../domain/deck/bringIn.js";
import type { CardId } from "../../domain/types/index.js";
import { loadStageProgress } from "../../infrastructure/playLogApi.js";
import { playGameSound } from "../../audio/gameSoundPlayer.js";
import { PoolPanel } from "./PoolPanel.js";
import { BossBriefing } from "./BossBriefing.js";

export function BringInScreen() {
  const { state, dispatch } = useGame();
  const [clearedCount, setClearedCount] = useState(0);
  const [selected, setSelected] = useState<CardId | null>(null);
  const stageId = state.bringInStageId ?? undefined;
  const stage = getStage(stageId);

  useEffect(() => {
    let cancelled = false;
    void loadStageProgress().then((progress) => {
      if (!cancelled) setClearedCount(countClearedStages(progress));
    });
    return () => { cancelled = true; };
  }, []);

  const canPick = (cardId: CardId) => isBringInUnlocked(cardId, clearedCount);
  const pickedName = (selected ? getCard(selected)?.name : null) ?? "未選択";

  return (
    <section className="screen bring-in-screen">
      <header className="bring-in-header">
        <p className="bring-in-kicker">{stage.name}</p>
        <h1>作戦会議</h1>
        <p className="bring-in-clear-badge">持ち込み解放 <strong>{clearedCount}</strong> / {bringInUnlockAt("paid-leave") ?? listStages().length}</p>
      </header>

      <section className="bring-in-section bring-in-pick-section">
        <h2>持ち込みカード（1枚・必須）</h2>
        <p className="bring-in-guide">
          {selected ? "1枚だけデッキに加えられます。" : "1枚選んでください。選ぶまで開始できません。"}
        </p>
        <div className={`bring-in-card-row${selected ? "" : " is-required"}`}>
          {BRING_IN_CARD_IDS.map((cardId) => {
            const def = getCard(cardId);
            if (!def) return null;
            const locked = !canPick(cardId);
            const remaining = (bringInUnlockAt(cardId) ?? 0) - clearedCount;
            return (
              <div key={cardId} className={`bring-in-card-slot${locked ? " is-locked" : ""}`}>
                <CardView
                  card={{ instanceId: `bring-in-${cardId}`, cardId }}
                  definition={def}
                  selectable={!locked}
                  picked={selected === cardId && !locked}
                  disabled={locked}
                  className="bring-in-card"
                  onClick={() => {
                    playGameSound("ui.select");
                    setSelected(cardId);
                  }}
                />
                {locked ? <span className="bring-in-lock">あと{remaining}ステージで解放</span> : null}
              </div>
            );
          })}
        </div>
      </section>

      <BossBriefing stage={stage} />

      <section className="bring-in-section">
        <h2>デッキ構成</h2>
        <ul className="bring-in-deck-chips">
          <li className="bring-in-deck-chip">
            <span className="bring-in-deck-name">新卒</span>
            <span className="bring-in-deck-count">×7</span>
          </li>
          <li className="bring-in-deck-chip">
            <span className="bring-in-deck-name">窓際</span>
            <span className="bring-in-deck-count">×2</span>
          </li>
          <li className="bring-in-deck-chip is-bring-in">
            <span className="bring-in-deck-name">{pickedName}</span>
            <span className="bring-in-deck-count">×1</span>
            <span className="bring-in-deck-tag">持ち込み</span>
          </li>
        </ul>
      </section>

      <PoolPanel stageId={stage.id} />

      <footer className="bring-in-footer">
        {selected ? null : (
          <p className="bring-in-start-note">持ち込みカードを1枚選んでください</p>
        )}
        <button
          type="button"
          className="btn btn-primary bring-in-start"
          disabled={!selected}
          onClick={() => {
            if (!selected) return;
            dispatch({ type: "START_NEW", stageId, bringInCardId: selected });
          }}
        >
          このデッキではじめる
        </button>
        <button type="button" className="btn" onClick={() => dispatch({ type: "OPEN_STAGE_SELECT" })}>
          ステージ選択へ戻る
        </button>
      </footer>
    </section>
  );
}
